import {
  Box,
  HStack,
  PopoverTrigger,
  Stack,
  StackProps,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { User, useUserCompaniesQuery, CompanyDetails, UserCompaniesDocument_ } from 'models';
import { useSwitchCompany } from './CompanySwitcher.hooks';
import { useCurrentCompany, useToast } from 'utils/hooks';
import { useTranslation } from 'utils/translation';
import { CompanyLogo, CompanyPresentation } from 'Organisms';
import { Button, EmptyState, IconButton, Input } from 'Atoms';
import { Typography } from 'Tokens';
import { Popover, PopoverContent, PopoverBody } from '@chakra-ui/react';
import { CompanyRegisterModal } from 'Features/CompanyForm';
import { Dispatch, SetStateAction, useMemo, useState, useRef, useEffect } from 'react';
import { useAuth } from 'Providers/auth/AuthProvider.tsx';
import {
  hasSalesOrSupportRole,
  hasSupportRole,
  isCelsiaSupport,
  isIssSales,
  isUserTwoWeeksOld,
} from 'utils/users';
import { RefreshIcon, SearchIcon, SortAscendingIcon, SortIcon } from 'Tokens/Icons/Function';
import { CompanyIcon } from 'Tokens/Icons/Data';
import { ChevronDownIcon } from 'Tokens/Icons/Direction';
import { CheckIcon } from 'Tokens/Icons/Status';
import { CircleIconWrapper } from 'Tokens/Icons/IconWrapper';
import { hotjar } from 'react-hotjar';
import { useNhostClient } from 'Providers/nhost/NhostProvider.tsx';
import { useApolloClient } from '@apollo/client';

interface CompanySwitcherProps extends StackProps {
  user: User;
}

const CompanyButton = ({
  company,
  select,
  isSelected,
}: {
  company: CompanyDetails;
  select: (c: CompanyDetails) => void;
  isSelected: boolean;
}) => {
  return (
    <Button
      _hover={{ bg: 'bg.hover' }}
      _focus={{ bg: 'bg.hover' }}
      onClick={() => select(company)}
      tabIndex={0}
      textTransform="none"
      justifyContent="flex-start"
      variant="ghost"
      borderRadius="lg"
      width="100%"
      height="36px"
      max-height="40px"
      position="relative"
    >
      <HStack width="100%" justify="space-between">
        <CompanyPresentation
          company={company}
          isSelected={isSelected}
          logoSize="28px"
          logoBorderRadius="6px"
        />
        {isSelected && <CheckIcon color="text.selected" />}
      </HStack>
    </Button>
  );
};

const CompanySwitcher = ({ user, ...rest }: CompanySwitcherProps) => {
  const nhost = useNhostClient();
  const { company: currentCompany } = useCurrentCompany();
  const [search, setSearch] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { user: currentUser } = useAuth();
  const { data: userCompanies } = useUserCompaniesQuery({
    variables: {
      id: user.id,
    },
  });
  const switchCompany = useSwitchCompany();
  const toast = useToast();
  const apolloClient = useApolloClient();

  const { isOpen, onOpen, onClose, onToggle } = useDisclosure();
  const { onOpen: onCreateOpen, isOpen: isCreateOpen, onClose: onCreateClose } = useDisclosure();
  const [isCompanyCreationLoading, setIsCompanyCreationLoading] = useState(false);

  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  const filteredCompanies = useMemo(() => {
    const companies = userCompanies?.data?.companies
      ?.filter((c) => c.company.name.toLowerCase().includes(search.toLowerCase()))
      ?.sort((a, b) => {
        const comparison = a.company.name.localeCompare(b.company.name);
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    return companies;
  }, [userCompanies, search, sortDirection]);

  const handleChange = (company: CompanyDetails) => {
    switchCompany(company.id);
    hotjar.identify(null, {
      isCelsia: isCelsiaSupport(currentUser?.email),
      isIss: isIssSales(currentUser?.email),
      isReal: company?.isReal,
      isTwoWeeksOld: isUserTwoWeeksOld(user?.createdAt),
      environment: import.meta.env.REACT_APP_NHOST_SUBDOMAIN,
    });
    onClose();
  };

  const handleSearchBlur = () => {
    setTimeout(() => {
      if (!search) {
        setSearchOpen(false);
      }
    }, 100);
  };

  const { t } = useTranslation('common');

  const onCreateEsrsDemoCompany = async (
    setIsCompanyCreationLoading: Dispatch<SetStateAction<boolean>>
  ) => {
    setIsCompanyCreationLoading(true);
    try {
      const res = await nhost.functions.post<{
        company: CompanyDetails;
        esrsAssessment: any;
      }>('esrs/generate-demo-company', {
        userId: user?.id,
      });
      toast({
        text: 'Demo company created.',
      });
      await nhost.refreshSession(0);
      await apolloClient.refetchQueries({ include: [UserCompaniesDocument_] });
    } catch (error) {
      console.error(error);
      toast({
        variant: 'danger',
        text: 'Unable to create demo company.',
      });
    }
    setIsCompanyCreationLoading(false);
  };

  if (!currentCompany) return null;
  return (
    <>
      <Popover
        isOpen={isOpen}
        onClose={onClose}
        placement="bottom-start"
        closeOnBlur={true}
        isLazy={true}
      >
        <PopoverTrigger>
          <HStack
            {...rest}
            as={Button}
            onClick={onToggle}
            gap="0px"
            maxW="240px"
            variant="ghost"
            size="sm"
            alignVertically
            rightIcon={<ChevronDownIcon />}
            bg="bg.interactive"
          >
            <Stack width="16px">
              {currentCompany.logoUrl ? (
                <CompanyLogo url={currentCompany.logoUrl} size="16px" borderRadius="4px" />
              ) : (
                <CircleIconWrapper
                  icon={CompanyIcon}
                  size="16px"
                  color="bg.default"
                  borderRadius="4px"
                />
              )}
            </Stack>
            <Typography
              title={currentCompany.name}
              whiteSpace="nowrap"
              variant="bodyStrong"
              overflow="hidden"
              textOverflow="ellipsis"
              pl="8px"
            >
              {currentCompany.name}
            </Typography>
          </HStack>
        </PopoverTrigger>

        <PopoverContent
          width="360px"
          height="600px"
          border="1px solid"
          borderColor="border.decorative"
          boxShadow="0 0 72px -2px rgba(15, 15, 46, 0.36), 0 6px 8px -2px rgba(15, 15, 46, 0.06), 0 0 0 1px rgba(15, 15, 46, 0.12)"
          bg="bg.default"
          color="black"
          overflowY="auto"
          overflowX="hidden"
        >
          <PopoverBody padding="8px">
            <VStack height="100%">
              <VStack width="100%" spacing="8px" h="100%">
                <VStack width="100%" gap="0px">
                  <HStack justifyContent="space-between" width="100%" padding="8px 12px" gap="0px">
                    <Typography variant="bodyStrong" color="text.default">
                      Your companies
                    </Typography>
                    <HStack>
                      <IconButton
                        aria-label="Search"
                        icon={<SearchIcon />}
                        variant="ghost"
                        size="sm"
                        onClick={() => setSearchOpen(!searchOpen)}
                      />
                      <IconButton
                        aria-label={sortDirection === 'asc' ? 'Sort Descending' : 'Sort Ascending'}
                        icon={sortDirection === 'asc' ? <SortAscendingIcon /> : <SortIcon />}
                        variant="ghost"
                        size="sm"
                        onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                      />
                    </HStack>
                  </HStack>
                  <Box
                    width="100%"
                    maxHeight={searchOpen ? '40px' : '0px'}
                    transition="max-height 0.25s cubic-bezier(0.4,0,0.2,1), opacity 0.25s"
                    overflow="hidden"
                    opacity={searchOpen ? 1 : 0}
                    padding="0px 8px"
                  >
                    {searchOpen && (
                      <Input
                        ref={searchInputRef}
                        placeholder="Search companies..."
                        value={search}
                        onChange={(e) => setSearch(e.currentTarget.value)}
                        onBlur={handleSearchBlur}
                        size="sm"
                        width="100%"
                      />
                    )}
                  </Box>
                </VStack>
                <VStack gap="12px" width="100%">
                  {filteredCompanies?.length ? (
                    filteredCompanies.map(({ company }) => (
                      <CompanyButton
                        key={company.id}
                        company={company}
                        select={handleChange}
                        isSelected={company.id === currentCompany.id}
                      />
                    ))
                  ) : (
                    <Box w="100%" py="16px">
                      <EmptyState
                        title={t('common:search.filter.emptyTitle')}
                        description={t('common:search.filter.emptyDescription')}
                        callToAction={{
                          text: t('common:search.filter.emptyBtn'),
                          variant: 'secondary',
                          onClick: () => {
                            setSearch('');
                          },
                          leftIcon: <RefreshIcon color="inherit" />,
                        }}
                        component={true}
                        width="100%"
                      />
                    </Box>
                  )}
                </VStack>
                {hasSupportRole(currentUser?.roles) && (
                  <Button width="full" mt="24px" onClick={onCreateOpen}>
                    {t('common:companyDrawer.createCompany')}
                  </Button>
                )}

                {hasSalesOrSupportRole(currentUser?.roles) && (
                  <Button
                    variant="ghost"
                    width="full"
                    onClick={() => onCreateEsrsDemoCompany(setIsCompanyCreationLoading)}
                    isLoading={isCompanyCreationLoading}
                    isDisabled={isCompanyCreationLoading}
                  >
                    Create ESRS demo company
                  </Button>
                )}
              </VStack>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Popover>
      <CompanyRegisterModal
        isOpen={isCreateOpen}
        onClose={() => {
          onCreateClose();
          onClose();
        }}
      />
    </>
  );
};

export default CompanySwitcher;
